import { RefObject, useLayoutEffect, useRef } from "react";
import { MotionValue } from "framer-motion";
import { useSceneRender } from "../SceneTrack";
import { box, clamp, easeOut, lerp, offsetRect, range, scramble } from "../morph";
import { badgeLink } from "../../Lanyard/badgeLink";
import "./IndexScene.css";

/*
 * 01 · Index — "Badge Handoff".
 *
 * Unlike the other scenes this one works on the real hero layout, not a design
 * canvas: the hero's name is the far end of Intro's shared-layout flight, and
 * the 3D lanyard frames itself on the hero's portrait slot every frame.
 *
 * That framing is what the scene uses. Moving and scaling the portrait slot
 * carries the 3D card itself to the middle of the page — there is only ever
 * one card on screen. Once it's there, a flat twin painted exactly like the
 * card's face takes its place, the 3D scene pauses, and the twin turns into
 * the info card: the hero's name, role and facts fly onto it. At the end the
 * name collapses to "QH" and docks into the navbar logo.
 *
 * The navbar is told through two custom properties on the navbar logo:
 *   --brand-dock  0→1  narrows the navbar's "QuocHuy" to "QH"
 *   --brand-mark  0→1  shows the navbar's own "QH"
 */

const CARD_W = 320;
const CARD_H = 440;
/** Where the face's photo window sits, as fractions of the card (from drawBadgeFace). */
const FACE_PHOTO = { x: 56 / 716, y: 150 / 1000, w: 604 / 716, h: 540 / 1000 };
/** Letters of "Quoc Huy" that fold away, in the order they go (inside out). */
const COLLAPSE_ORDER: Record<number, number> = { 4: 0, 3: 1, 2: 2, 1: 3, 6: 4, 7: 5 };
const BAR_WIDTHS = [2, 1, 3, 1, 2, 2, 1, 3, 1, 2, 1, 1, 3, 2];
const STATUS = "AVAILABLE FOR WORK";

type Rect = { x: number; y: number; w: number; h: number };

interface Measured {
    stage: HTMLElement;
    stageW: number;
    stageH: number;
    navH: number;
    name: HTMLElement;
    nameRect: Rect;
    font: number;
    letters: HTMLElement[];
    widths: number[];
    role: HTMLElement | null;
    roleRect: Rect;
    facts: HTMLElement[];
    factRects: Rect[];
    /** Each fact's left padding (the 2-column phone strip pads only the right column). */
    factPads: number[];
    factList: HTMLElement | null;
    slot: HTMLElement | null;
    slotRect: Rect;
    eyebrow: HTMLElement | null;
    lede: HTMLElement | null;
    cta: HTMLElement | null;
    cue: HTMLElement | null;
}

/**
 * The navbar logo's two custom properties. They are set on the logo elements
 * themselves, not on <html>: a custom property inherits, so writing one on the
 * root re-styled every element on the page (≈1,100 of them, 15ms+ a time) on
 * each frame the value moved — the main cost of a fast scroll through Index.
 * Only write when the value actually changes.
 */
const lastVar: Record<string, string> = {};
const logos = () => Array.from(document.querySelectorAll<HTMLElement>(".logo-swap"));
function setBrandVar(name: string, value: number) {
    const v = value.toFixed(3);
    if (lastVar[name] === v) return;
    lastVar[name] = v;
    logos().forEach((el) => el.style.setProperty(name, v));
}

/** Fade-and-lift for the hero copy that leaves. Clears its inline styles at 0
 * so the hero's own entrance animation keeps ownership until scrolling starts. */
function leave(el: HTMLElement | null, t: number, dy: number, blur = 0) {
    if (!el) return;
    if (t <= 0) {
        el.style.opacity = "";
        el.style.transform = "";
        el.style.filter = "";
        return;
    }
    el.style.opacity = String(1 - t);
    el.style.transform = `translateY(${dy * easeOut(t)}px)`;
    el.style.filter = blur ? `blur(${blur * t}px)` : "";
}

interface IndexMorphProps {
    p: MotionValue<number>;
    heroRef: RefObject<HTMLElement | null>;
    /** Re-measure when the hero swaps which element renders the name. */
    mode: string;
    photo: string;
    /**
     * Phone hero: there is no photo and no 3D card to hand off from, so the
     * dark info card fades in directly and the photo first appears on it.
     */
    phone?: boolean;
}

export default function IndexMorph({ p, heroRef, mode, photo, phone = false }: IndexMorphProps) {
    const badgeRef = useRef<HTMLDivElement>(null);
    const M = useRef<Measured | null>(null);
    /** The card's box in the slot's own (untransformed) space. */
    const cardInSlot = useRef<Rect | null>(null);
    /** The slot transform the last render applied. */
    const slotT = useRef({ tx: 0, ty: 0, s: 1 });
    const paused = useRef(false);
    /** Where the 3D card was on screen when the twin took over (stage px). */
    const handoff = useRef<Rect | null>(null);

    const measure = () => {
        const hero = heroRef.current;
        const badge = badgeRef.current;
        const stage = badge?.parentElement;
        const name = hero?.querySelector<HTMLElement>('[data-im="name"]');
        if (!hero || !stage || !name) return;
        const q = (k: string) => hero.querySelector<HTMLElement>(`[data-im="${k}"]`);
        const facts = Array.from(hero.querySelectorAll<HTMLElement>('[data-im="fact"]'));
        const role = q("role");
        // A hidden slot (the phone hero has no photo) has nothing to carry.
        const slotEl = q("slot");
        const slot = slotEl && slotEl.getClientRects().length ? slotEl : null;
        const letters = Array.from(name.querySelectorAll<HTMLElement>("[data-glyph]"));
        const h1 = name.querySelector("h1") ?? name;
        M.current = {
            stage,
            stageW: stage.clientWidth,
            stageH: stage.clientHeight,
            navH: (document.querySelector(".navbar") as HTMLElement | null)?.offsetHeight ?? 64,
            name,
            nameRect: offsetRect(name, stage),
            font: parseFloat(getComputedStyle(h1).fontSize) || 112,
            letters,
            widths: letters.map((l) => l.offsetWidth),
            role,
            roleRect: role ? offsetRect(role, stage) : { x: 0, y: 0, w: 1, h: 1 },
            facts,
            factRects: facts.map((f) => offsetRect(f, stage)),
            factPads: facts.map((f) => parseFloat(getComputedStyle(f).paddingLeft) || 0),
            factList: q("facts"),
            slot,
            slotRect: slot ? offsetRect(slot, stage) : { x: stage.clientWidth - 400, y: 160, w: 300, h: 375 },
            eyebrow: q("eyebrow"),
            lede: q("lede"),
            cta: q("cta"),
            cue: q("cue"),
        };
        cardInSlot.current = null;
    };

    const render = (v: number) => {
        const m = M.current;
        const badge = badgeRef.current;
        if (!m || !badge) return;

        // Hero copy that has no place on the card leaves first.
        leave(m.eyebrow, range(v, 0.06, 0.14), -16);
        leave(m.lede, range(v, 0.08, 0.18), -20, 4);
        leave(m.cta, range(v, 0.1, 0.2), -12, 4);
        if (m.cue) m.cue.style.opacity = v > 0 ? String(1 - range(v, 0.06, 0.1)) : "";

        const cardX = m.stageW / 2 - CARD_W / 2;
        const cardY = clamp((m.stageH - CARD_H) / 2 + 16, m.navH + 16, Math.max(m.navH + 16, m.stageH - CARD_H - 16));

        /* ---- Pull: the 3D card itself travels to the middle ---- */
        badgeLink.steady = range(v, 0.04, 0.16);
        const k = easeOut(range(v, 0.06, 0.3));
        const sr = m.slotRect;
        // Learn where the card hangs inside the slot while the slot is (nearly)
        // still, then keep that reading: once the slot is moving, the 3D frame
        // reports a frame late and the reading would wobble.
        if (k <= 0.02 || !cardInSlot.current) {
            const r = badgeLink.setPaused ? badgeLink.rect : null;
            if (r && r.h > 0) {
                const st = m.stage.getBoundingClientRect();
                const t = slotT.current;
                cardInSlot.current = {
                    x: (r.x - st.left - sr.x - t.tx) / t.s,
                    y: (r.y - st.top - sr.y - t.ty) / t.s,
                    w: r.w / t.s,
                    h: r.h / t.s,
                };
            } else if (k <= 0.02) {
                // No 3D card (touch, no WebGL, still loading): carry the photo.
                cardInSlot.current = { x: 0, y: 0, w: sr.w, h: sr.h };
            }
        }
        const local = cardInSlot.current ?? { x: 0, y: 0, w: sr.w, h: sr.h };
        const sT = CARD_H / Math.max(1, local.h);
        const s = lerp(1, sT, k);
        const tx = lerp(0, cardX + CARD_W / 2 - sr.x - sT * (local.x + local.w / 2), k);
        const ty = lerp(0, cardY + CARD_H / 2 - sr.y - sT * (local.y + local.h / 2), k);
        slotT.current = k > 0 ? { tx, ty, s } : { tx: 0, ty: 0, s: 1 };
        if (m.slot) {
            m.slot.style.transform = k > 0 ? `translate(${tx}px,${ty}px) scale(${s})` : "";
            // The lanyard multiplies this into its own overlay's opacity.
            const out = range(v, 0.3, 0.33);
            m.slot.style.opacity = out > 0 ? String(1 - out) : "";
        }

        /* ---- Handoff: the flat twin replaces the 3D card, which then pauses ---- */
        const shouldPause = v >= 0.34;
        if (shouldPause !== paused.current && badgeLink.setPaused) {
            paused.current = shouldPause;
            badgeLink.setPaused(shouldPause);
        }

        // Only inside the hold does the card swing, and only with scroll speed.
        const w = range(v, 0.66, 0.7) * (1 - range(v, 0.82, 0.86));
        const th = w ? clamp(-p.getVelocity() * 4, -3, 3) * w : 0;
        const pvx = m.stageW / 2;
        const pvy = -40;
        const swing = (ox: number, oy: number) =>
            th ? `translate(${pvx - ox}px,${pvy - oy}px) rotate(${th}deg) translate(${ox - pvx}px,${oy - pvy}px) ` : "";

        // The flat twin appears exactly where the 3D card is on screen at the
        // moment of handoff — wherever that is, even mid-swing — then flies to
        // the middle. Starting it at the middle instead left the two cards
        // overlapping whenever the 3D one hadn't quite arrived.
        if (v < 0.29) {
            handoff.current = null;
        } else if (!handoff.current) {
            const r = badgeLink.setPaused ? badgeLink.rect : null;
            if (r && r.w > 0 && r.h > 0) {
                const st = m.stage.getBoundingClientRect();
                handoff.current = { x: r.x - st.left, y: r.y - st.top, w: r.w, h: r.h };
            } else {
                handoff.current = { x: cardX, y: cardY, w: CARD_W, h: CARD_H };
            }
        }
        const h0 = handoff.current ?? { x: cardX, y: cardY, w: CARD_W, h: CARD_H };
        if (phone) {
            const ka = easeOut(range(v, 0.22, 0.34));
            const sc = lerp(0.92, 1, ka);
            badge.style.visibility = v >= 0.22 ? "visible" : "hidden";
            badge.style.opacity = String(range(v, 0.22, 0.3));
            badge.style.transform = `${swing(0, 0)}translate(${cardX + (CARD_W * (1 - sc)) / 2}px,${cardY + (CARD_H * (1 - sc)) / 2}px) scale(${sc})`;
        } else {
            const hk = easeOut(range(v, 0.3, 0.4));
            badge.style.visibility = v >= 0.3 ? "visible" : "hidden";
            badge.style.opacity = String(range(v, 0.3, 0.32));
            badge.style.transform = `${swing(0, 0)}translate(${lerp(h0.x, cardX, hk)}px,${lerp(h0.y, cardY, hk)}px) scale(${lerp(h0.w / CARD_W, 1, hk)},${lerp(h0.h / CARD_H, 1, hk)})`;
        }

        /* ---- Morph: the card face turns into the info card ---- */
        const q = (k2: string) => badge.querySelector<HTMLElement>(`[data-ib="${k2}"]`)!;
        q("face").style.opacity = phone ? "0" : String(1 - range(v, 0.36, 0.48));
        q("print").style.opacity = phone ? "0" : String(1 - range(v, 0.34, 0.4));
        const kp = phone ? 1 : easeOut(range(v, 0.38, 0.52));
        if (phone) {
            const ki = easeOut(range(v, 0.48, 0.58));
            q("photo").style.opacity = String(ki);
            q("photo").style.transform = `scale(${lerp(0.85, 1, ki)})`;
        }
        box(
            q("photo"),
            lerp(FACE_PHOTO.x * CARD_W, 24, kp),
            lerp(FACE_PHOTO.y * CARD_H, 44, kp),
            lerp(FACE_PHOTO.w * CARD_W, 104, kp),
            lerp(FACE_PHOTO.h * CARD_H, 128, kp)
        );
        const d = range(v, 0.4, 0.52);
        const rect = badge.querySelector<SVGRectElement>("rect")!;
        rect.style.strokeDashoffset = String(1 - d);
        rect.style.opacity = String(d <= 0 ? 0 : 1 - 0.72 * range(v, 0.52, 0.6));
        scramble(q("status"), STATUS, range(v, 0.56, 0.64));
        Array.from(q("bars").children).forEach((b, i) => {
            (b as HTMLElement).style.transform = `scaleY(${easeOut(range(v, 0.58 + i * 0.006, 0.62 + i * 0.006))})`;
        });
        q("name").style.opacity = String(range(v, 0.95, 1));

        /* ---- Name → card → navbar ---- */
        const nr = m.nameRect;
        const kn = easeOut(range(v, 0.4, 0.52));
        const kd = easeOut(range(v, 0.9, 0.98));
        let nx = lerp(nr.x, cardX + 144, kn);
        let ny = lerp(nr.y, cardY + 52, kn);
        let ns = lerp(1, 33 / m.font, kn);
        if (kd > 0) {
            const logo = document.querySelector(".navbar .logo-swap");
            if (logo) {
                const lr = logo.getBoundingClientRect();
                const st = m.stage.getBoundingClientRect();
                const ts = (lr.height * 1.05) / (m.font * 0.92);
                nx = lerp(nx, lr.left - st.left, kd);
                ny = lerp(ny, lr.top - st.top + lr.height / 2 - (m.font * 0.92 * ts) / 2, kd);
                ns = lerp(ns, ts, kd);
            }
        }
        const moved = kn > 0;
        m.name.style.transform = moved ? `${swing(nr.x, nr.y)}translate(${nx - nr.x}px,${ny - nr.y}px) scale(${ns})` : "";
        m.name.style.opacity = moved ? String(1 - range(v, 0.97, 1)) : "";

        // Glyph collapse: "Quoc Huy" → "QH".
        let shift = 0;
        m.letters.forEach((el, i) => {
            const order = COLLAPSE_ORDER[i];
            const c = order === undefined ? 0 : easeOut(range(v, 0.84 + order * 0.012, 0.88 + order * 0.012));
            el.style.transform = c || shift ? `translateX(${-shift}px) scaleX(${1 - c})` : "";
            el.style.opacity = c ? String(1 - c) : "";
            shift += m.widths[i] * c;
        });
        setBrandVar("--brand-dock", easeOut(range(v, 0.9, 0.97)));
        setBrandVar("--brand-mark", range(v, 0.96, 1));

        if (m.role) {
            const rr = m.roleRect;
            const kr = easeOut(range(v, 0.42, 0.54));
            const rs = lerp(1, Math.min(0.7, 150 / Math.max(1, rr.w)), kr);
            m.role.style.transform = kr
                ? `${swing(rr.x, rr.y)}translate(${lerp(0, cardX + 144 - rr.x, kr)}px,${lerp(0, cardY + 96 - rr.y, kr)}px) scale(${rs})`
                : "";
        }

        const kl = easeOut(range(v, 0.44, 0.52));
        if (m.factList) m.factList.style.borderTopColor = kl ? `rgba(255,255,255,${0.105 * (1 - kl)})` : "";
        m.facts.forEach((f, i) => {
            const fr = m.factRects[i];
            const kf = easeOut(range(v, 0.46 + i * 0.03, 0.58 + i * 0.03));
            if (!kf) {
                f.style.transform = "";
                f.style.borderLeftColor = "";
                f.style.borderTopColor = "";
                return;
            }
            const pad = m.factPads[i];
            const fx = cardX + 24 - pad * 0.85;
            const fy = cardY + 188 + i * 46;
            f.style.transform = `${swing(fr.x, fr.y)}translate(${lerp(0, fx - fr.x, kf)}px,${lerp(0, fy - fr.y, kf)}px) scale(${lerp(1, 0.85, kf)})`;
            f.style.borderLeftColor = `rgba(255,255,255,${0.105 * (1 - kf)})`;
            f.style.borderTopColor = `rgba(255,255,255,${0.105 * (1 - kf)})`;
        });
    };

    useLayoutEffect(() => {
        const badge = badgeRef.current;
        const stage = badge?.parentElement;
        if (!stage) return;
        const remeasure = () => {
            measure();
            render(p.get());
        };
        remeasure();
        const ro = new ResizeObserver(remeasure);
        ro.observe(stage);
        let cancelled = false;
        document.fonts?.ready.then(() => !cancelled && remeasure());
        return () => {
            cancelled = true;
            ro.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    useSceneRender(p, render);

    // Leave the rest of the page as it was found.
    useLayoutEffect(
        () => () => {
            logos().forEach((el) => {
                el.style.removeProperty("--brand-dock");
                el.style.removeProperty("--brand-mark");
            });
            delete lastVar["--brand-dock"];
            delete lastVar["--brand-mark"];
            badgeLink.steady = 0;
            badgeLink.setPaused?.(false);
        },
        []
    );

    return (
        <div ref={badgeRef} className="ib" aria-hidden="true">
            {/* The card's printed face, as the 3D card shows it (drawBadgeFace). */}
            <div className="ib-face" data-ib="face">
                <span className="ib-band" />
                <span className="ib-print" data-ib="print">
                    <b>QUOC HUY</b>
                    <span>FULL-STACK DEVELOPER</span>
                    <i />
                    <small>QUY NHON · VIETNAM</small>
                </span>
            </div>
            <span className="ib-punch" />
            <div className="ib-photo" data-ib="photo">
                <img src={photo} alt="" />
            </div>
            <span className="ib-name" data-ib="name">
                Quoc Huy
            </span>
            <span className="ib-status mono" data-ib="status" />
            <span className="ib-bars" data-ib="bars">
                {BAR_WIDTHS.map((bw, i) => (
                    <i key={i} style={{ width: bw }} />
                ))}
            </span>
            <svg className="ib-outline" viewBox="-1 -1 322 442">
                <rect x="0" y="0" width={CARD_W} height={CARD_H} rx="14" pathLength={1} />
            </svg>
        </div>
    );
}
